/**
 * Ticket List Page
 * ================
 * Paginated list of all tickets with filters and sorting.
 */

import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  MessageSquare,
  LinkIcon,
} from 'lucide-react'
import Button from '@/components/Button'
import { useTickets, useTicketStats } from '@/hooks/useTickets'
import {
  TICKET_STATUS_LABELS,
  TICKET_STATUS_COLORS,
  TICKET_PRIORITY_LABELS,
  TICKET_PRIORITY_DOT_COLORS,
  TICKET_CATEGORY_LABELS,
  TICKET_SOURCE_LABELS,
} from '@/types'
import type {
  TicketSearchParams,
  TicketSortField,
} from '@/types'

// Filter options
const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'new', label: 'New' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'waiting_external', label: 'Waiting' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
]

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
]

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'hardware', label: 'Hardware' },
  { value: 'software', label: 'Software' },
  { value: 'network', label: 'Network' },
  { value: 'personal', label: 'Personal' },
  { value: 'other', label: 'Other' },
]

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 7) return `${diffDay}d ago`
  return d.toLocaleDateString()
}

export default function TicketList() {
  const navigate = useNavigate()
  const [urlParams] = useSearchParams()
  const assetIdParam = urlParams.get('asset_id')
  const [searchInput, setSearchInput] = useState('')
  const [searchParams, setSearchParams] = useState<TicketSearchParams>({
    page: 1,
    limit: 20,
    sort_by: 'created_at',
    sort_order: 'desc',
    ...(assetIdParam ? { asset_id: Number(assetIdParam) } : {}),
  })

  const { data, isLoading } = useTickets(searchParams)
  const { data: statsData } = useTicketStats()

  const tickets = data?.data ?? []
  const meta = data?.meta
  const stats = statsData?.data

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSearchParams((prev) => ({ ...prev, search: searchInput || undefined, page: 1 }))
  }

  const handleFilterChange = (key: string, value: string) => {
    setSearchParams((prev) => ({
      ...prev,
      [key]: value || undefined,
      page: 1,
    }))
  }

  const handleSort = (field: TicketSortField) => {
    setSearchParams((prev) => ({
      ...prev,
      sort_by: field,
      sort_order: prev.sort_by === field && prev.sort_order === 'asc' ? 'desc' : 'asc',
    }))
  }

  const SortIcon = ({ field }: { field: TicketSortField }) => {
    if (searchParams.sort_by !== field) return <ChevronDown size={14} className="opacity-30" />
    return searchParams.sort_order === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
  }

  // Quick stat counts
  const newCount = stats?.by_status?.new ?? 0
  const inProgressCount = stats?.by_status?.in_progress ?? 0
  const blockedCount = stats?.by_status?.blocked ?? 0
  const resolvedCount = stats?.by_status?.resolved ?? 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tickets</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {meta ? `${meta.total} ticket${meta.total !== 1 ? 's' : ''}` : 'Loading...'}
          </p>
        </div>
        <Button icon={<Plus size={16} />} onClick={() => navigate('/tickets/new')}>
          New Ticket
        </Button>
      </div>

      {/* Quick Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <button
            onClick={() => handleFilterChange('status', 'new')}
            className="bg-white dark:bg-gray-800 rounded-lg p-4 border dark:border-gray-700 text-left hover:ring-2 hover:ring-blue-300 transition"
          >
            <p className="text-sm text-gray-500 dark:text-gray-400">New</p>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{newCount}</p>
          </button>
          <button
            onClick={() => handleFilterChange('status', 'in_progress')}
            className="bg-white dark:bg-gray-800 rounded-lg p-4 border dark:border-gray-700 text-left hover:ring-2 hover:ring-yellow-300 transition"
          >
            <p className="text-sm text-gray-500 dark:text-gray-400">In Progress</p>
            <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{inProgressCount}</p>
          </button>
          <button
            onClick={() => handleFilterChange('status', 'blocked')}
            className="bg-white dark:bg-gray-800 rounded-lg p-4 border dark:border-gray-700 text-left hover:ring-2 hover:ring-red-300 transition"
          >
            <p className="text-sm text-gray-500 dark:text-gray-400">Blocked</p>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">{blockedCount}</p>
          </button>
          <button
            onClick={() => handleFilterChange('status', 'resolved')}
            className="bg-white dark:bg-gray-800 rounded-lg p-4 border dark:border-gray-700 text-left hover:ring-2 hover:ring-green-300 transition"
          >
            <p className="text-sm text-gray-500 dark:text-gray-400">Resolved</p>
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{resolvedCount}</p>
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-end">
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-primary-500 w-64"
            />
          </div>
          <Button type="submit" variant="secondary" size="sm">
            Search
          </Button>
        </form>

        <select
          value={(searchParams.status as string) ?? ''}
          onChange={(e) => handleFilterChange('status', e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <select
          value={(searchParams.priority as string) ?? ''}
          onChange={(e) => handleFilterChange('priority', e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        >
          {PRIORITY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <select
          value={(searchParams.category as string) ?? ''}
          onChange={(e) => handleFilterChange('category', e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        >
          {CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        {/* Clear filters */}
        {(searchParams.status || searchParams.priority || searchParams.category || searchParams.search) && (
          <button
            onClick={() => {
              setSearchInput('')
              setSearchParams({ page: 1, limit: 20, sort_by: 'created_at', sort_order: 'desc' })
            }}
            className="text-sm text-primary-600 hover:underline dark:text-primary-400"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading tickets...</div>
        ) : tickets.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            No tickets found.{' '}
            <button
              onClick={() => navigate('/tickets/new')}
              className="text-primary-600 hover:underline dark:text-primary-400"
            >
              Create one?
            </button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">
                  <button onClick={() => handleSort('ticket_id')} className="flex items-center gap-1 hover:text-gray-900 dark:hover:text-white">
                    ID <SortIcon field="ticket_id" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">
                  <button onClick={() => handleSort('title')} className="flex items-center gap-1 hover:text-gray-900 dark:hover:text-white">
                    Title <SortIcon field="title" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">
                  <button onClick={() => handleSort('status')} className="flex items-center gap-1 hover:text-gray-900 dark:hover:text-white">
                    Status <SortIcon field="status" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400">
                  <button onClick={() => handleSort('priority')} className="flex items-center gap-1 hover:text-gray-900 dark:hover:text-white">
                    Priority <SortIcon field="priority" />
                  </button>
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 hidden md:table-cell">
                  Category
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 hidden lg:table-cell">
                  Asset
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-400 hidden sm:table-cell">
                  <button onClick={() => handleSort('created_at')} className="flex items-center gap-1 hover:text-gray-900 dark:hover:text-white">
                    Created <SortIcon field="created_at" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => (
                <tr
                  key={ticket.id}
                  onClick={() => navigate(`/tickets/${ticket.ticket_id}`)}
                  className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400">
                    {ticket.ticket_id}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-900 dark:text-white font-medium truncate max-w-xs">
                        {ticket.title}
                      </span>
                      {ticket.comment_count > 0 && (
                        <span className="flex items-center gap-0.5 text-xs text-gray-400">
                          <MessageSquare size={12} />
                          {ticket.comment_count}
                        </span>
                      )}
                      {ticket.source !== 'manual' && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                          {TICKET_SOURCE_LABELS[ticket.source]}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TICKET_STATUS_COLORS[ticket.status]}`}>
                      {TICKET_STATUS_LABELS[ticket.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${TICKET_PRIORITY_DOT_COLORS[ticket.priority]}`} />
                      <span className="text-gray-700 dark:text-gray-400 text-sm">
                        {TICKET_PRIORITY_LABELS[ticket.priority]}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-gray-600 dark:text-gray-400">
                    {ticket.category ? TICKET_CATEGORY_LABELS[ticket.category] : '-'}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    {ticket.asset_name ? (
                      <span className="flex items-center gap-1 text-gray-600 dark:text-gray-400 text-sm">
                        <LinkIcon size={12} />
                        {ticket.asset_name}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-gray-500 dark:text-gray-400 text-sm">
                    {formatDate(ticket.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {meta && meta.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Page {meta.page} of {meta.total_pages} ({meta.total} total)
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.has_prev}
                onClick={() => setSearchParams((prev) => ({ ...prev, page: (prev.page ?? 1) - 1 }))}
                icon={<ChevronLeft size={16} />}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.has_next}
                onClick={() => setSearchParams((prev) => ({ ...prev, page: (prev.page ?? 1) + 1 }))}
              >
                Next <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
